// Typed campaign endpoint functions. Backed by GET /api/v1/campaigns/*.
//
// Per CHG-1236 plan §1.1 — consumed by @generated/screens/campaign-list.ts +
// campaign-detail.ts queryFn callbacks. Spec:
// specs/tier-04-interface/campaign-management.api.yaml.

import { apiClient } from "./client";
import type {
  Campaign,
  CampaignDonationSummary,
  CampaignDonorSummary,
  CampaignDesignationLink,
  CampaignTimelineEvent,
  PagedEnvelope,
} from "./types";

export interface ListCampaignsParams {
  page?: number;
  pageSize?: number;
  /** Accepted for caller compat; the live GET /campaigns list has no sort
   *  param (page/per_page/search/status only) so these are not forwarded. */
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
}

export async function listCampaigns(
  params: ListCampaignsParams = {},
): Promise<{ items: Campaign[]; total: number }> {
  // CHG-CAMP-005 (R13) — match the live FastAPI router contract: request
  // page/per_page/search/status (page is 1-based, ge=1; the v2 screens are
  // 0-based) and read the {data, pagination} envelope (CampaignPagedEnvelope).
  // The wrapper still returns {items, total} for its v2 callers.
  const r = await apiClient().get<{ data: Campaign[]; pagination: { total: number } }>(
    "/campaigns",
    {
      params: {
        page: (params.page ?? 0) + 1,
        per_page: params.pageSize ?? 50,
        search: params.search,
        status: params.status,
      },
    },
  );
  return { items: r.data.data, total: r.data.pagination.total };
}

export async function getCampaign(id: string): Promise<Campaign> {
  const r = await apiClient().get<Campaign>(`/campaigns/${id}`);
  return r.data;
}

export async function getCampaignDonations(
  id: string,
): Promise<PagedEnvelope<CampaignDonationSummary>> {
  const r = await apiClient().get<PagedEnvelope<CampaignDonationSummary>>(
    `/campaigns/${id}/donations`,
  );
  return r.data;
}

export async function getCampaignDonors(
  id: string,
): Promise<PagedEnvelope<CampaignDonorSummary>> {
  const r = await apiClient().get<PagedEnvelope<CampaignDonorSummary>>(
    `/campaigns/${id}/donors`,
  );
  return r.data;
}

export async function getCampaignTimeline(
  id: string,
): Promise<CampaignTimelineEvent[]> {
  const r = await apiClient().get<CampaignTimelineEvent[]>(`/campaigns/${id}/timeline`);
  return r.data;
}

// /campaigns/{id}/designations isn't a v4-canonical endpoint yet — Campaign
// ↔ Designation linkage lives on the Designation side (Designation.campaign_id).
// The Designations tab calls this stub for now; backend follow-up CHG
// surfaces a real list endpoint mirroring the donations / donors pattern.
export async function getCampaignDesignations(
  id: string,
): Promise<PagedEnvelope<CampaignDesignationLink>> {
  // Fallback to the designations list endpoint filtered by campaign_id.
  const r = await apiClient().get<PagedEnvelope<CampaignDesignationLink>>(
    `/designations`,
    { params: { campaign_id: id } },
  );
  return r.data;
}

export const campaignsApi = {
  listCampaigns,
  getCampaign,
  getCampaignDonations,
  getCampaignDonors,
  getCampaignTimeline,
  getCampaignDesignations,
};
